import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-textarea-input',
  templateUrl: './textarea-input.component.html',
  standalone: true,
  styleUrls: ['./textarea-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaInputComponent),
      multi: true
    }
  ],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Default
})
export class TextareaInputComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = '';
  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Input() maxLength: number | null = null;
  @Input() showCharCount = true;
  @Input() labelId: string | null = null;

  @ViewChild('content', { static: false }) content: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('input', { static: false }) input: ElementRef<HTMLInputElement> | undefined;

  protected cdr = inject(ChangeDetectorRef);

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public writeValue(value: string | null): void {
    this.value = value === null ? '' : value;
    this.updateValue();
  }

  private updateValue(): void {
    if (!!this.content && !!this.input) {
      this.content.nativeElement.innerText = this.value;
      this.input.nativeElement.value = this.value;
      this.cdr.detectChanges();
    }
  }

  public handleInput(event: Event): void {
    let inputValue = this.content?.nativeElement.innerText ?? '';
    if (inputValue === '\n') inputValue = '';
    if (this.maxLength !== null && inputValue.length > this.maxLength) {
      inputValue = inputValue.slice(0, this.maxLength);
      if (this.content) {
        this.content.nativeElement.innerText = inputValue;
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(this.content.nativeElement);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
    this.value = inputValue;
    this.onChange(inputValue);
  }

  public handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event?.clipboardData?.getData('text/plain');
    document.execCommand('insertText', false, text);
  }

  public handleBlur(): void {
    this.onTouched();
  }

  public ngAfterViewInit(): void {
    this.updateValue();
  }
}
